/**
 * IconCache - Efficient SVG icon loading and caching system
 *
 * This system loads each SVG icon once and creates lightweight DOM clones
 * instead of making HTTP requests for every icon instance. This significantly
 * reduces network traffic and CPU load when rendering multiple icons.
 */

import { STATICS_DIR } from '@constants/global.js';

class IconCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Load and cache an SVG icon
   * @param {string} iconName - Name of the icon file (without .svg extension)
   * @returns {Promise<SVGElement>} The cached SVG element
   */
  async loadIcon(iconName) {
    // Return cached icon if available
    if (this.cache.has(iconName)) {
      return this.cache.get(iconName);
    }

    // Return loading promise if already loading
    if (this.loadingPromises.has(iconName)) {
      return this.loadingPromises.get(iconName);
    }

    // Create loading promise
    const loadingPromise = this._fetchAndCacheIcon(iconName);
    this.loadingPromises.set(iconName, loadingPromise);

    try {
      const svgElement = await loadingPromise;
      this.cache.set(iconName, svgElement);
      this.loadingPromises.delete(iconName);
      return svgElement;
    } catch (error) {
      this.loadingPromises.delete(iconName);
      throw error;
    }
  }

  /**
   * Create a clone of a cached icon with optional customization
   * @param {string} iconName - Name of the icon
   * @param {Object} options - Customization options
   * @param {string} options.color - Fill color
   * @param {string} options.strokeColor - Stroke color
   * @param {number} options.size - Size in pixels
   * @returns {Promise<SVGElement>} Cloned and customized SVG element
   */
  async createIcon(iconName, options = {}) {
    const { color = 'currentColor', strokeColor = null, size = null } = options;

    const cachedSvg = await this.loadIcon(iconName);
    const clonedSvg = cachedSvg.cloneNode(true);

    // Apply customizations
    if (color !== 'none') {
      clonedSvg.setAttribute('fill', color);
      // Remove fill from child elements to ensure parent fill takes precedence
      const elements = clonedSvg.querySelectorAll('*');
      elements.forEach(el => el.removeAttribute('fill'));
    }

    if (strokeColor) {
      clonedSvg.setAttribute('stroke', strokeColor);
    }

    if (size) {
      clonedSvg.setAttribute('width', `${size}px`);
      clonedSvg.setAttribute('height', `${size}px`);
    } else {
      clonedSvg.setAttribute('width', '100%');
      clonedSvg.setAttribute('height', '100%');
    }

    return clonedSvg;
  }

  /**
   * Private method to fetch and parse SVG
   * @param {string} iconName - Name of the icon file
   * @returns {Promise<SVGElement>} Parsed SVG element
   * @private
   */
  async _fetchAndCacheIcon(iconName) {
    const svgUrl = `${STATICS_DIR}images/icons/${iconName}.svg`;

    try {
      const response = await fetch(svgUrl);
      if (!response.ok) {
        throw new Error(`Failed to load icon: ${iconName}`);
      }

      const svgContent = await response.text();

      if (!svgContent.trim().startsWith('<svg')) {
        throw new Error(`Invalid SVG content for icon: ${iconName}`);
      }

      const svgElement = new DOMParser().parseFromString(
        svgContent,
        'image/svg+xml',
      ).documentElement;

      if (svgElement.nodeName === 'parsererror') {
        throw new Error(`Error parsing SVG for icon: ${iconName}`);
      }

      return svgElement;
    } catch (error) {
      console.error(`Error loading icon ${iconName}:`, error);
      throw error;
    }
  }

  /**
   * Preload multiple icons
   * @param {string[]} iconNames - Array of icon names to preload
   * @returns {Promise<void>}
   */
  async preloadIcons(iconNames) {
    const loadPromises = iconNames.map(iconName => this.loadIcon(iconName));
    await Promise.all(loadPromises);
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      cachedIcons: this.cache.size,
      loadingIcons: this.loadingPromises.size,
      iconNames: Array.from(this.cache.keys()),
    };
  }
}

// Create and export singleton instance
const iconCache = new IconCache();
export default iconCache;
